class HeatMapHelper {
    ////////////////////////////////////////////////////////////////////////////////
    // Ratios...
    
    static MIN_RATE = 0.001;
    static MAX_RATE = 1;

    ////////////////////////////////////////////////////////////////////////////////
    // Slice and Dice
    
    static resolve(items, total, x1, x2, y1, y2) {
        let out = [];
        
        // Safety check: return empty array if no items or invalid input
        if (!items || items.length === 0) {
            return out;
        }
        
        // Safety check: prevent infinite recursion with depth limit
        if (HeatMapHelper.resolve.depth === undefined) {
            HeatMapHelper.resolve.depth = 0;
        }
        if (HeatMapHelper.resolve.depth > 100) { // reasonable recursion limit
            HeatMapHelper.resolve.depth = 0; // reset for next call
            return out;
        }
        
        if (items.length === 1) {
            let item = items[0];
            item.setX1(Math.round(x1));
            item.setX2(Math.round(x2));
            item.setY1(Math.round(y1));
            item.setY2(Math.round(y2));
            out.push(item);
            HeatMapHelper.resolve.depth = 0; // reset depth counter
            return out;
        }

        try {
            // Increment recursion depth
            HeatMapHelper.resolve.depth++;
            
            let half = Math.floor(total / 2);
            let w1 = 0;
            let w2 = 0;
            let i1 = [];
            let i2 = [];

            for (let i = 0; i < items.length; i++) {
                let item = items[i];
                let w = item.getValue();
                let w1_w = w1 + w;
                if (i1.length === 0 || w1_w <= half) {
                    w1 = w1_w;
                    i1.push(item);
                }
                else {
                    w2 = w2 + w;
                    i2.push(item);
                }
            }
            if (i1.length > 0 || i2.length > 0) {
                let width = x2 - x1;
                let height = y2 - y1;

                let set1x1, set1x2, set1y1, set1y2;
                let set2x1, set2x2, set2y1, set2y2;
                if (height >= width) { // Split up and down
                    // No Change on X-axis
                    set1x1 = x1;
                    set1x2 = x2;
                    set2x1 = x1;
                    set2x2 = x2;

                    // Change on Y-axis
                    let rate = w1 / total;
                    let minrate = HeatMapHelper.MIN_RATE;
                    let maxrate = HeatMapHelper.MAX_RATE;
                    if (i1.length === 1 && i2.length === 1) {
                        if (rate < minrate) {
                            rate = minrate;
                        }
                        else if (rate > maxrate) {
                            rate = maxrate;
                        }
                    }

                    let middlept = rate * height;
                    set1y1 = y1;
                    set1y2 = y1 + middlept;
                    set2y1 = y1 + middlept;
                    set2y2 = y2;
                }
                else { // Split left and right
                    // No Change on Y-axis
                    set1y1 = y1;
                    set1y2 = y2;
                    set2y1 = y1;
                    set2y2 = y2;

                    // Change on X-axis
                    let rate = w1 / total;
                    let minrate = HeatMapHelper.MIN_RATE;
                    let maxrate = HeatMapHelper.MAX_RATE;
                    if (i1.length === 1 && i2.length === 1) {
                        if (rate < minrate) {
                            rate = minrate;
                        }
                        else if (rate > maxrate) {
                            rate = maxrate;
                        }
                    }

                    let middlept = rate * width;
                    set1x1 = x1;
                    set1x2 = x1 + middlept;
                    set2x1 = x1 + middlept;
                    set2x2 = x2;
                }

                if (i1.length > 0) {
                    out = out.concat(HeatMapHelper.resolve(i1, w1, set1x1, set1x2, set1y1, set1y2));
                }
                if (i2.length > 0) {
                    out = out.concat(HeatMapHelper.resolve(i2, w2, set2x1, set2x2, set2y1, set2y2));
                }
            }
        }
        catch (ex) {
            // Handle exception silently as in original Java code
            // Reset depth counter on exception
            HeatMapHelper.resolve.depth = 0;
        }
        finally {
            // Decrement recursion depth
            HeatMapHelper.resolve.depth--;
            if (HeatMapHelper.resolve.depth < 0) {
                HeatMapHelper.resolve.depth = 0;
            }
        }

        return out;
    }

    static isDrawVertically(width, height) {
        return (width >= height);
    }

    static getScaleValues(items, width, height) {
        let total = 0;
        let scale;
        for (let item of items) {
            total = total + item.getValue();
        }

        // Every value is zero (or unusable): fall back to an even split so the
        // rate below stays a number instead of 0 / 0.
        if (!isFinite(total) || total <= 0) {
            for (let item of items) {
                item.setScaledValue((width * height) / items.length);
            }
            return;
        }

        ////////////////////////////////////////////////////////////////////
        // Adjusting total to control max and min sizes
        
        let aTotal = 0; //adjusted total
        let maxRate = HeatMapHelper.MAX_RATE;
        let minRate = HeatMapHelper.MIN_RATE;
        let tValue;
        let rate;
        for (let item of items) {
            tValue = item.getValue();
            rate = tValue / total;
            if (rate >= maxRate) {
                tValue = maxRate * total;
            }
            else if (rate < minRate) {
                tValue = minRate * total;
            }
            item.setScaledValue(tValue);
            aTotal = aTotal + tValue;
        }

        /////////////////////////////////////////////////////////////////////
        
        if (aTotal === 0) { //avoid divide by zero
            aTotal = 1;
        }
        scale = (width * height) / aTotal;
        for (let item of items) {
            item.setScaledValue(item.getScaledValue() * scale);
        }
    }

    ////////////////////////////////////////////////////////////////////////////////
    // Squarified
    
    static squarify(items, x1, x2, y1, y2) {
        let out = [];
        let width = x2 - x1;
        let height = y2 - y1;
        let tAspect = 999;
        let lAspect;
        let passes = 1;
        let i = 1;
        let yOffSet = 0;
        let xOffSet = 0;
        let tWidth;
        let tHeight;
        let total;

        // Safety check: return empty array if no items
        if (!items || items.length === 0) {
            return out;
        }

        // Safety check: a zero or negative box has no valid layout. Laying out
        // inside one divides by zero and produces NaN / negative coordinates.
        if (!isFinite(width) || !isFinite(height) || width <= 0 || height <= 0) {
            return out;
        }

        HeatMapHelper.getScaleValues(items, width, height);
        items.sort((a, b) => b.getScaledValue() - a.getScaledValue());

        // Add maximum iteration counter to prevent infinite loops
        const maxIterations = items.length * 10; // reasonable upper bound
        let iterationCount = 0;

        for (; i <= items.length && iterationCount < maxIterations; i++, iterationCount++) {

            // The remaining strip is exhausted; carrying on would divide by zero.
            if (width <= 0 || height <= 0) {
                break;
            }

            ///////////////////////////////////////////////////////////////////////////////////

            tWidth = 0;
            tHeight = 0;
            let tpasses = passes;
            //let tpasses = (items.length === passes) ? passes : passes - 1;
            total = 0;

            for (let t = 0; t < tpasses; t++) {
                total = total + items[t].getScaledValue();
            }

            if (HeatMapHelper.isDrawVertically(width, height)) { //it is vertical
                tWidth = total / height;
                for (let t = 0; t < tpasses; t++) {
                    tHeight = height * (items[t].getScaledValue() / total);
                }
            }
            else {
                tHeight = total / width;
                for (let t = 0; t < tpasses; t++) {
                    tWidth = width * (items[t].getScaledValue() / total);
                }
            }

            let aspectHW = tHeight / tWidth;
            let aspectWH = tWidth / tHeight;
            //lAspect = Math.max((tHeight / tWidth), (tWidth / tHeight));
            lAspect = (aspectHW > aspectWH) ? aspectHW : aspectWH;

            //////////////////////////////////////////////////////////////////////////////////////////
            
            if (lAspect > tAspect || i === items.length) {
                tAspect = 999;

                //////////////////////////////////////////////////////////////////////////////////////
                
                let tempHeight = 0;
                let tempWidth = 0;
                //tWidth = 0;
                //tHeight = 0;
                total = 0;
                tpasses = (items.length === passes) ? passes : passes - 1;

                for (let t = 0; t < tpasses; t++) {
                    total = total + items[t].getScaledValue();
                }
                if (HeatMapHelper.isDrawVertically(width, height)) { //it is vertical
                    tWidth = total / height;
                    for (let t = 0; t < tpasses; t++) {
                        tHeight = height * (items[t].getScaledValue() / total);
                        items[t].setX1(Math.round(xOffSet * 10000) / 10000);
                        items[t].setX2(Math.round((xOffSet + tWidth) * 10000) / 10000);
                        items[t].setY1(Math.round((yOffSet + tempHeight) * 10000) / 10000);
                        items[t].setY2(Math.round((yOffSet + tempHeight + tHeight) * 10000) / 10000);
                        out.push(items[t]);
                        tempHeight = tempHeight + tHeight;
                    }
                    xOffSet = xOffSet + tWidth;
                    width = width - tWidth;
                }
                else {
                    tHeight = total / width;
                    for (let t = 0; t < tpasses; t++) {
                        tWidth = width * (items[t].getScaledValue() / total);
                        items[t].setX1(Math.round((xOffSet + tempWidth) * 10000) / 10000);
                        items[t].setX2(Math.round((xOffSet + tempWidth + tWidth) * 10000) / 10000);
                        items[t].setY1(Math.round(yOffSet * 10000) / 10000);
                        items[t].setY2(Math.round((yOffSet + tHeight) * 10000) / 10000);
                        out.push(items[t]);
                        tempWidth = tempWidth + tWidth;
                    }
                    yOffSet = yOffSet + tHeight;
                    height = height - tHeight;
                }
                
                //////////////////////////////////////////////////////////////////////////////////////////
                
                passes = 1;
                if (items.length === i) {
                    items.length = 0; // Clear array
                    break; // Exit loop when array is cleared
                }
                else {
                    // Validate array slicing to prevent invalid access
                    if (i - 1 < items.length && i - 1 >= 0) {
                        items = items.slice(i - 1);
                    } else {
                        // If slicing would be invalid, exit loop
                        break;
                    }
                }
                i = 0;
            }
            else {
                passes++;
                tAspect = lAspect;
            }
        }
        return out;
    }
}

// Export for use in modules
if (typeof module !== 'undefined' && module.exports) {
    module.exports = HeatMapHelper;
}

// Export for ES6 modules
export default HeatMapHelper; 